import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Send, MessageSquareQuote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { REVIEWS, BOOKS } from '../mock/mock';
import { PageHero } from './BuyABook';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reviews = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', rating: 0, message: '', book: 'Limpid Reader' });
  const [hover, setHover] = useState(0);
  const [approved, setApproved] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setApproved(data || []);
    } catch {
      setApproved([]);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.rating) {
      toast({ title: 'Please fill out the form correctly.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, form);
      toast({ title: 'Review submitted!', description: data.message });
      setForm({ name: '', email: '', rating: 0, message: '', book: 'Limpid Reader' });
    } catch (err) {
      toast({ title: 'Could not submit review.', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Combine reader-approved reviews (from backend) with curated testimonials
  const readerReviews = approved.map((r) => ({
    key: r.id,
    name: r.name,
    rating: r.rating,
    text: r.message,
    book: r.book || 'Limpid Reader',
  }));
  const curated = REVIEWS.map((r, i) => ({ key: `curated-${i}`, ...r }));
  const allReviews = [...readerReviews, ...curated].filter((r) => r.text);

  return (
    <div className="fade-in">
      <PageHero title="Ratings & Reviews" subtitle="Real words from readers of Limpid Reader." />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Reviews list */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <MessageSquareQuote className="h-6 w-6 text-brand-gold" />
              <h2 className="font-serif text-3xl font-bold text-brand-ink">Reviews</h2>
            </div>
            <div className="section-divider mb-8" />
            <div className="space-y-6">
              {allReviews.map((r) => (
                <div key={r.key} className="bg-white rounded-2xl p-7 shadow-md">
                  <div className="flex gap-1 mb-3 items-center">
                    {Array.from({ length: Math.max(0, Math.min(5, r.rating)) }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-brand-gold text-brand-gold" />
                    ))}
                    <span className="ml-2 text-xs text-brand-brown/60">{r.rating}.0 out of 5</span>
                  </div>
                  <p className="text-brand-brown/90 leading-relaxed">“{r.text}”</p>
                  <p className="mt-4 font-serif font-bold text-brand-ink">— {r.name}</p>
                  <p className="text-xs text-brand-brass italic">on {r.book}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Feedback form */}
          <div>
            <div className="bg-white rounded-2xl p-8 shadow-md sticky top-32">
              <h3 className="font-serif text-2xl font-bold text-brand-ink">Leave a Review</h3>
              <p className="text-sm text-brand-brown/70 mt-1 mb-6">
                Share your thoughts — reviews appear here once approved.
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label className="text-brand-ink">Name (required)</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 bg-cream border-brand-goldlight"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label className="text-brand-ink">Email (required)</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 bg-cream border-brand-goldlight"
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <Label className="text-brand-ink">Which book?</Label>
                  <Select value={form.book} onValueChange={(v) => setForm({ ...form, book: v })}>
                    <SelectTrigger className="mt-1.5 bg-cream border-brand-goldlight">
                      <SelectValue placeholder="Select a book" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Limpid Reader">Limpid Reader (general)</SelectItem>
                      {BOOKS.map((b) => (
                        <SelectItem key={b.id} value={b.title}>
                          {b.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-brand-ink">Rate your experience (required)</Label>
                  <div className="flex gap-1.5 mt-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setForm({ ...form, rating: n })}
                      >
                        <Star
                          className={`h-7 w-7 transition-colors ${
                            (hover || form.rating) >= n
                              ? 'fill-brand-gold text-brand-gold'
                              : 'text-brand-goldlight'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-brand-ink">Your review</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1.5 bg-cream border-brand-goldlight"
                    rows={4}
                    placeholder="Share what you thought..."
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-11 gap-2"
                >
                  <Send className="h-4 w-4" /> {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
